import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { currentBaseUrl } from '../../helpers/urlConst';
import { fetchScript } from '../../hooks/useScripts';
import { useProduct } from '../../hooks/useProduct';
import usePortalSettings from '../../hooks/usePortalSettings';
import ShippingMethodImage from '../../commons/components/shipping-method-image';
import DataContext from '../../store/dataContext';
import UiContext from '../../store/uiContext';
import { paymentIdList } from '../../helpers/constants';
import useCartTotalsUtils from '../../hooks/useCartTotalsUtils';

import Cart from '../Cart';
import ContinueShoppingBtn from '../Button/ContinueShoppingBtn/index';

import { ReactComponent as SuccessIcon } from './icon/thank-you.svg';

import './styles.scss';

const OrderDisplay = ({ orderInfo }) => {
  const { t } = useTranslation();
  const dataContext = useContext(DataContext);
  const { setShowExtraInfoBackground, setHasExtraInfo } = useContext(UiContext);
  const { portalSettings } = usePortalSettings();
  const { allDigital } = useProduct();
  const { getOrderTotal } = useCartTotalsUtils();

  const apiOrder = orderInfo.order;
  const nonCreditCardMethod = orderInfo.paymentMethod;
  const cardType = apiOrder?.card?.issuer?.name;

  const renderBillingHeader = () => {
    const c1 = apiOrder?.billAddress && apiOrder?.billName;
    const c2 = orderInfo?.paymentMethodId;
    const shouldRender = c1 || c2;
    if (shouldRender) {
      return <p className="address-title billing-info-title">{t('order_display.billing_information')}</p>;
    }
    return null;
  };

  const renderPayedWith = () => {
    const c1 = nonCreditCardMethod;
    const c2 = orderInfo?.paymentMethodId;
    const shouldRenderNonCredit = c1 || c2;
    if (shouldRenderNonCredit) {
      switch (orderInfo?.paymentMethodId) {
        case paymentIdList.PAYPAL:
          return (
            <div className="payment-container">
              <img className="paypal-logo" alt="PayPal logo" src={`${currentBaseUrl()}assets/paypal-logo.png`} />
              <span>{apiOrder?.payer?.email_address}</span>
            </div>
          );
        case paymentIdList.AMAZONPAY:
          return (
            <div className="payment-container">
              <img className="amazon-logo" alt="Amazon logo" src={`${currentBaseUrl()}assets/amazonpay-text.svg`} />
              <span>{apiOrder?.payer?.email_address}</span>
            </div>
          );
        default:
          return (
            <span>
              <span className="payment-method">{nonCreditCardMethod}</span>
            </span>
          );
      }
    }
    if (getOrderTotal() > 0) {
      return (
        <span>
          <span className="protected">••••-</span>
          {apiOrder?.card?.last4}
        </span>
      );
    }
    return null;
  };

  useEffect(() => {
    const { href, search, protocol, host, pathname } = window.location;
    if (
      href.indexOf('/checkout/order-confirmation') === -1 &&
      portalSettings?.scripts?.find(
        item => item?.scriptLocation === 'All pages' || item?.scriptLocation === 'Order confirmation',
      )
    ) {
      if (portalSettings?.scripts?.find(item => item?.parent)) {
        const parentUrl = `${dataContext.storeUrl}/checkout/order-confirmation?preventRefresh=true`;
        window.parent.postMessage(
          JSON.stringify({
            task: 'changeUrl',
            url: parentUrl,
          }),
          '*',
        );
        window.parent.postMessage(
          JSON.stringify({
            task: 'createElement',
            id: 'order_id',
            value: `order_id: ${apiOrder?.platformOrderId}`,
            type: 'hidden',
          }),
          '*',
        );
        window.parent.postMessage(
          JSON.stringify({
            task: 'removeAllScripts',
          }),
          '*',
        );
      }
      const newUrl = `${protocol}//${host}/checkout/order-confirmation${pathname}${search}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
    window.parent.postMessage(
      JSON.stringify({
        task: 'removeAllScripts',
      }),
      '*',
    );
    for (let x = 0; x < portalSettings?.scripts?.length; x++) {
      const script = document.getElementById(`script-${x}`);
      if (script) {
        try {
          document.head.removeChild(script);
        } catch (err) {
          // err
        }
      }
      const setting = portalSettings?.scripts[x];
      if (setting.scriptLocation === 'All pages' || setting.scriptLocation === 'Order confirmation') {
        if (setting?.parent) {
          window.parent.postMessage(
            JSON.stringify({
              task: 'appendScript',
              url: setting?.url,
              content: setting?.content,
              type: setting?.type?.toLowerCase(),
            }),
            '*',
          );
        } else {
          let value = apiOrder?.platformOrderId;
          if (setting?.param?.toLowerCase() === 'order_id' || setting?.param?.toLowerCase() === 'orderId') {
            value = apiOrder?.platformOrderId;
          }

          if (setting.name === 'Beam' || setting.name?.indexOf('dynamic script') > -1) {
            setShowExtraInfoBackground(true);
          }

          if (
            setting.name === 'Beam' ||
            setting.name?.indexOf('Bing') > -1 ||
            setting.name?.indexOf('Ad') > -1 ||
            setting.name?.indexOf('dynamic script') > -1
          ) {
            setHasExtraInfo(true);
            fetchScript(setting, x, apiOrder);
          } else {
            fetchScript(setting, x, value);
          }
        }
      }
    }
    return () => {
      for (let x = 0; x < portalSettings?.scripts?.length; x++) {
        const script = document.getElementById(`script-${x}`);
        if (script) {
          try {
            document.head.removeChild(script);
          } catch (err) {
            // err
          }
        }
      }
    };
  }, [portalSettings, apiOrder?.platformOrderId]);

  const clearEstimated = text => text?.replace('Est. ', '');

  return (
    <div id="order-display" className="section">
      {apiOrder ? (
        <div className="order-display-wrapper">
          <div className="order-display-header">
            <SuccessIcon />
            <div>
              <h1 className="order-title">{t('order_display.thank_you')}</h1>
              <input type="hidden" value={`order_id: ${apiOrder?.platformOrderId}`} />
              <p className="order-message">{t('order_display.your_order_has_been_placed')}</p>
              <div id="order_id" className="order-details">
                {t('order_display.order_number')}
                <span>{apiOrder?.platformOrderId}</span>
              </div>
            </div>
          </div>

          <div className="user-details">
            <p className="address-title">{t('order_display.shipping_information')}</p>
            {!allDigital() && (
              <p className="name">
                {apiOrder?.shipName}
                <span>{apiOrder.shipCompany ? ` - ${apiOrder.shipCompany}` : null}</span>
              </p>
            )}
            {!allDigital() && (
              <>
                <p className="address">{apiOrder?.shipAddress?.userInputSingleLine}</p>
                <p className="address">{apiOrder?.shipAddress?.line2}</p>
              </>
            )}
            {!allDigital() && <p>{apiOrder?.shipAddress?.billPhone}</p>}
            <p className="email">{apiOrder?.email}</p>
          </div>
          <br />
          {!allDigital() && apiOrder?.shipAddress && apiOrder.shippingMethod && (
            <div className="shipping-details">
              <p className="address-title">{t('order_display.shipping_method')}</p>
              <div className="shipping-company-and-method">
                <ShippingMethodImage type={apiOrder.shippingMethod?.type} label={apiOrder.shippingMethod?.label} />
                <h2>{apiOrder?.shippingMethod?.label}</h2>
              </div>
              {apiOrder?.shippingMethod?.transitTime && (
                <div className="shipping-time">
                  <p className="transit-text">{t('order_display.estimated_arrival')}</p>
                  <p className="transit-time">{clearEstimated(apiOrder.shippingMethod.transitTime)}</p>
                </div>
              )}
            </div>
          )}
          {renderBillingHeader()}
          <div className="card-info">
            {cardType && cardType !== 'alt' && (
              <img alt={cardType} src={`${currentBaseUrl()}card-logos/${cardType}.svg`} className="card-type" />
            )}
            {renderPayedWith()}
          </div>
          {apiOrder?.billName && apiOrder?.billAddress?.userInputSingleLine && (
            <>
              <p className="name">{apiOrder.billName}</p>
              <p className="address">{apiOrder.billAddress.userInputSingleLine}</p>
              <p className="phone">{dataContext.billingPhone}</p>
            </>
          )}
          <Cart type="order" />
          {dataContext.orderNotes && (
            <>
              <br />
              <p className="address-title">Notes</p>
              <p className="transit-text">{dataContext.orderNotes}</p>
            </>
          )}
          <ContinueShoppingBtn />
        </div>
      ) : (
        <>
          <div className="section-header">
            <h1>{`${t('order_display.redirecting')} ${t('order_display.thank_you')}`}</h1>
          </div>
          <div className="order-message">{t('order_display.your_order_has_been_placed')}</div>
          <ContinueShoppingBtn />
        </>
      )}
    </div>
  );
};

OrderDisplay.propTypes = {
  orderInfo: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default OrderDisplay;
